import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Adds items to the cart using the theme's cart event, so the cart drawer
 * re-renders and opens exactly like a normal "Add to cart".
 *
 * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
 * @param {HTMLElement} source - Element that dispatches the cart events.
 * @returns {Promise<any>}
 */
export function addToCart(items, source) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  const root = window.Shopify?.routes?.root || '/';

  return fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds.join(',') }),
  })
    .then((response) => response.json())
    .then(async (response) => {
      const ajaxCart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const failed = Boolean(response.status);

      if (failed) {
        source.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-extras',
          sourceId: source.id || 'cart-extras',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });

      if (failed) throw new Error(response.description || response.message || 'Add to cart failed');
      return response;
    })
    .catch((error) => {
      deferred.reject(error);
      throw error;
    });
}

/**
 * "Your order is reserved for 09:58" countdown.
 * The end time is kept in sessionStorage so it does not restart when the drawer re-renders.
 */
class CartReserveTimer extends HTMLElement {
  connectedCallback() {
    const minutes = Number(this.dataset.minutes) || 10;
    const key = 'cart-reserve-end';
    let end = 0;
    try { end = Number(sessionStorage.getItem(key)) || 0; } catch (e) {}
    if (!end || end < Date.now()) {
      end = Date.now() + minutes * 60 * 1000;
      try { sessionStorage.setItem(key, String(end)); } catch (e) {}
    }
    this.end = end;
    this.out = this.querySelector('[data-time]');
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  tick() {
    const s = Math.max(0, Math.round((this.end - Date.now()) / 1000));
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    if (this.out) this.out.textContent = `${mm}:${ss}`;
    if (s === 0) clearInterval(this.interval);
  }
}

/** "Add to cart" button of an order bump inside the cart drawer. */
class CartUpsellButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.select = this.querySelector('select');
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  onClick = () => {
    if (!this.button || this.button.disabled) return;
    const id = Number(this.select?.value || this.dataset.variantId);
    if (!id) return;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    addToCart([{ id, quantity: 1 }], this)
      .catch((error) => {
        console.warn('[cart-upsell]', error);
        if (this.isConnected && this.button) {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        }
      });
  };
}

if (!customElements.get('cart-reserve-timer')) customElements.define('cart-reserve-timer', CartReserveTimer);
if (!customElements.get('cart-upsell-button')) customElements.define('cart-upsell-button', CartUpsellButton);
